import { viewportBounds } from '@opendoor/shared-ts/geo-viewport';
import { z } from 'zod';

import { normalizeSearchVertices } from '../helpers/useSearchQueries';
import type { GeoPoint, MapBounds, MarketShape, SearchMapHandle } from './types';

export const getBoundsFromShape = (shape: MarketShape): MapBounds => {
  const points = shape.coordinates.flat();
  if (points.length === 0) {
    throw new Error('Invalid polygon coordinates');
  }

  const viewport = viewportBounds(points);
  if (viewport && viewport.west > viewport.east) {
    return [
      [viewport.west, viewport.south],
      [viewport.east, viewport.north],
    ];
  }

  const lngs = points.map((coord) => coord.lon);
  const lats = points.map((coord) => coord.lat);

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

/** Zeus accepts a closed polygon of 4 to 256 points. */
const MAX_VIEWPORT_POINTS = 256;

const closeRing = (points: GeoPoint[]): GeoPoint[] => {
  const first = points[0];
  const last = points.at(-1);
  if (first && last && (first.lat !== last.lat || first.lon !== last.lon)) {
    return [...points, first];
  }
  return points;
};

const cross = (o: GeoPoint, a: GeoPoint, b: GeoPoint) =>
  (a.lon - o.lon) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lon - o.lon);

/** Andrew's monotone chain. Returns the hull counter-clockwise, without repeating the start. */
export const convexHull = (points: GeoPoint[]): GeoPoint[] => {
  const sorted = [...points].sort((a, b) => a.lon - b.lon || a.lat - b.lat);
  if (sorted.length < 3) {
    return sorted;
  }
  const lower: GeoPoint[] = [];
  for (const point of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: GeoPoint[] = [];
  for (const point of [...sorted].reverse()) {
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

/**
 * A closed polygon for the area a market shape covers, usable as an adaptive
 * map viewport. It is the convex hull of the shape, so it contains every home
 * the market search lists while spilling far less into neighbouring markets
 * than a bounding box would. Hulls beyond Zeus's point limit and shapes too
 * small to form a ring fall back to their bounding box to preserve coverage.
 */
export const getViewportFromShape = (shape: MarketShape): GeoPoint[] => {
  const points = shape.coordinates.flat();
  const viewport = viewportBounds(points);
  if (viewport && viewport.west > viewport.east) {
    return points;
  }
  const hull = convexHull(points);
  if (hull.length >= 3 && hull.length < MAX_VIEWPORT_POINTS) {
    return closeRing(hull);
  }
  const [[west, south], [east, north]] = getBoundsFromShape(shape);
  return [
    { lon: west, lat: north },
    { lon: east, lat: north },
    { lon: east, lat: south },
    { lon: west, lat: south },
    { lon: west, lat: north },
  ];
};

const wrapLongitude = (longitude: number) => ((((longitude + 180) % 360) + 360) % 360) - 180;

export const getShapeFromMap = (map: Pick<SearchMapHandle, 'getBounds'>): GeoPoint[] => {
  const bounds = map.getBounds();
  if (bounds === null) {
    throw new Error('Map bounds are not available');
  }
  const northWest = bounds.getNorthWest();
  const vertices = [
    { lon: wrapLongitude(northWest.lng), lat: northWest.lat },
    {
      lon: wrapLongitude(bounds.getNorthEast().lng),
      lat: bounds.getNorthEast().lat,
    },
    {
      lon: wrapLongitude(bounds.getSouthEast().lng),
      lat: bounds.getSouthEast().lat,
    },
    {
      lon: wrapLongitude(bounds.getSouthWest().lng),
      lat: bounds.getSouthWest().lat,
    },
    // The first and last points of a polygon must be identical
    { lon: wrapLongitude(northWest.lng), lat: northWest.lat },
  ];
  return vertices;
};

const geoJsonPositionSchema = z.tuple([z.number(), z.number()]).rest(z.number());
const zoneBoundingBoxSchema = z.object({
  coordinates: z.array(z.array(geoJsonPositionSchema)).min(1),
  type: z.literal('Polygon'),
});

const parseZoneBoundingBox = (boundingBox: string | null): GeoPoint[] | null => {
  if (!boundingBox) {
    return null;
  }

  let value: unknown;
  try {
    value = JSON.parse(boundingBox);
  } catch {
    return null;
  }

  const parsed = zoneBoundingBoxSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  const ring = parsed.data.coordinates[0];
  if (!ring) {
    return null;
  }

  return normalizeSearchVertices(ring.map(([lon, lat]) => ({ lat, lon })));
};

// A box across the antimeridian (Alaska) keeps only its two edge longitudes, so it no longer
// says where the zone is. Frame a state-sized area around the zone's center instead.
const CENTER_FRAME_HALF_SPAN = { lat: 7, lon: 15 };

export const getZoneFrameBounds = ({
  boundingBox,
  center,
}: {
  boundingBox: string | null;
  center: { latitude: number; longitude: number } | null;
}): MapBounds | null => {
  const vertices = parseZoneBoundingBox(boundingBox);
  if (!vertices) {
    return null;
  }
  const bounds = getBoundsFromShape({ coordinates: [vertices], type: 'Polygon' });
  if (bounds[1][0] - bounds[0][0] <= 180) {
    return bounds;
  }
  if (!center) {
    return null;
  }
  return [
    [center.longitude - CENTER_FRAME_HALF_SPAN.lon, center.latitude - CENTER_FRAME_HALF_SPAN.lat],
    [center.longitude + CENTER_FRAME_HALF_SPAN.lon, center.latitude + CENTER_FRAME_HALF_SPAN.lat],
  ];
};
